import {Component, ElementRef, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {NgIf} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";

@Component({
    selector: 'app-signature',
    standalone: true,
    templateUrl: './signature.component.html',
    imports: [
        NgIf,
        MatIcon,
        MatButton
    ],
    styleUrls: ['./signature.component.css']
})
export class SignatureComponent implements OnInit{
    @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;
    @Output() signaturePresent: EventEmitter<boolean> = new EventEmitter();
    @Output() signatureDone: EventEmitter<string> = new EventEmitter();
    private ctx!: CanvasRenderingContext2D;
    private drawing = false;
    private lastX = 0;
    private lastY = 0;
    private lastTimestamp = 0;
    private lastPressure = 1;
    protected signatureIntroduced = false;
    protected signatureUpdated = false;


    ngOnInit() {
        this.ctx = this.canvas.nativeElement.getContext('2d')!;
        this.ctx.lineCap = 'round';
        this.ctx.strokeStyle = '#003bff';
    }

    startDrawing(event: MouseEvent | TouchEvent) {
        this.drawing = true;
        const { offsetX, offsetY, pressure, timestamp } = this.getPosition(event);
        this.ctx.beginPath();
        this.ctx.moveTo(offsetX, offsetY);
        this.lastX = offsetX;
        this.lastY = offsetY;
        this.lastPressure = pressure;
        this.lastTimestamp = timestamp;
    }

    draw(event: MouseEvent | TouchEvent) {
        if (!this.drawing) return;
        if(!this.signatureIntroduced){
            this.signatureIntroduced = true;
        }

        this.signatureUpdated=true;
        const { offsetX, offsetY, pressure, timestamp } = this.getPosition(event);

        // Calculate speed based on movement distance and time
        const distance = Math.sqrt((offsetX - this.lastX) ** 2 + (offsetY - this.lastY) ** 2);
        const timeElapsed = timestamp - this.lastTimestamp;
        const speed = timeElapsed > 0 ? distance / timeElapsed : 0;

        // Adjust stroke width based on speed (faster movement = thinner stroke)
        const minWidth = 1;
        const maxWidth = 3;
        const normalizedSpeed = Math.max(0, Math.min(1, 1 - speed / 3)); // Normalize speed
        this.ctx.lineWidth = minWidth + (maxWidth - minWidth) * normalizedSpeed * pressure;

        this.ctx.lineTo(offsetX, offsetY);
        this.ctx.stroke();

        this.lastX = offsetX;
        this.lastY = offsetY;
        this.lastPressure = pressure;
        this.lastTimestamp = timestamp;
    }

    stopDrawing() {
        this.drawing = false;
    }

    clearCanvas() {
        this.ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
        if(this.signatureIntroduced){
            this.signaturePresent.emit(false);
            this.signatureIntroduced = false;
            this.signatureUpdated = false;
        }
    }

    getPosition(event: MouseEvent | TouchEvent) {
        const rect = this.canvas.nativeElement.getBoundingClientRect();
        let offsetX, offsetY, pressure = 1, timestamp = event.timeStamp;

        if ('touches' in event) {
            const touch = event.touches[0];
            offsetX = touch.clientX - rect.left;
            offsetY = touch.clientY - rect.top;
            pressure = touch.force || 1;
        } else {
            offsetX = (event as MouseEvent).offsetX;
            offsetY = (event as MouseEvent).offsetY;
            pressure = (event as MouseEvent).buttons ? 1 : 0.5;
        }

        return { offsetX, offsetY, pressure, timestamp };
    }

    private getCompressedCanvasData(): string {
        return this.canvas.nativeElement.toDataURL('image/webp', 0.8);
    }

    private getSignatureAsSvgString(): string {
        const canvas = this.canvas.nativeElement;
        return `<svg width="${canvas.width}" height="${canvas.height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="white"/>
      <image href="${this.getCompressedCanvasData()}" width="${canvas.width}" height="${canvas.height}"/>
    </svg>`;
    }

    logSignature() {
        console.log(this.hasSignature());
        console.log(this.getSignatureAsSvgString());
    }

    saveAsSvg() {
        const svgString = this.getSignatureAsSvgString();
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'signature.svg';
        link.click();
    }

    hasSignature(): boolean {
        const canvas = this.canvas.nativeElement;
        const imageData = this.ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        for (let i = 0; i < pixels.length; i += 4) {
            // Alpha channel is in pixels[i + 3], non-zero means drawn content exists
            if (pixels[i + 3] !== 0) {
                return true; // Found a non-transparent pixel
            }
        }

        return false; // No signature detected
    }

    sendSignature (){
        this.signatureUpdated = false;
        this.signaturePresent.emit(true);
        this.signatureDone.emit(this.getSignatureAsSvgString());
    }

    protected readonly console = module
}
